<script setup lang="ts">
/**
 * BannerSection —— 首页顶部全宽轮播（F11，2026-06-22 Q8 + 2026-06-24 Q7）。
 *
 * 复用 `Section.images: { url, caption? }[]` 渲染 autoplay 轮播，零新增
 * 后端实体（Q8 决策）。结构视觉对齐 `project/index.html` `.banner-carousel`：
 * 全宽 viewport + 横向 `transform: translateX` 切换 + dots 指示器 + 左右
 * prev/next 控件 + hover 暂停 autoplay（无障碍友好）。
 *
 * **全宽 Section**（Q7 决策）：组件根级 CSS Modules 设 `grid-column: 1 / -1`，
 * 占满 HomePage 主容器 grid 整行，不参与 PC 端双列布局（ADR-0012）。
 *
 * **Fallback**：空 / null images → Section-level Failure 占位卡
 * （`copy.sections.banner.dataMissing`）；单图 `@error` → 复用 F07
 * `<FallbackImage>` Asset-level 降级（占位图 + console.error 含 url，
 * url 编进 context 串透传给 useAssetFallback）。
 */
import { computed, onMounted, onUnmounted, ref } from 'vue';

import FallbackImage from '@/components/FallbackImage.vue';
import { copy } from '@/i18n/zh-CN';
import { hasText, sectionImages } from './section-presenters';
import type { SectionComponentProps } from './section-props';

const props = defineProps<SectionComponentProps>();

/** Autoplay 切图间隔（手风琴模式下调整为 5000ms）。 */
const AUTOPLAY_INTERVAL_MS = 5000;
/** 单图轮播无需 autoplay / prev-next，长度阈值常量化。 */
const SINGLE_IMAGE = 1;
/** 首屏可见轮播图必须脱离 lazy 队列，避免 LCP 图片排在后续轮播图之后。 */
const FIRST_SLIDE_INDEX = 0;
/** carousel-inner 单张占满视口宽度，translateX 按此百分比步进。 */
const SLIDE_WIDTH_PERCENT = 100;

const images = computed(() => sectionImages(props.section));
const current = ref(0);
const paused = ref(false);
let timer: ReturnType<typeof setInterval> | null = null;

/** 多图（>1）才需要 prev/next/dots 控件与 autoplay；单图渲染静态封面。 */
const hasControls = computed(() => images.value.length > SINGLE_IMAGE);

const innerStyle = computed(() => ({
  transform: `translateX(-${current.value * SLIDE_WIDTH_PERCENT}%)`,
}));

function goTo(index: number): void {
  current.value = index;
}

function next(): void {
  const total = images.value.length;
  current.value = (current.value + 1) % total;
}

function prev(): void {
  const total = images.value.length;
  current.value = (current.value - 1 + total) % total;
}

function handleItemClick(index: number): void {
  if (images.value.length >= 3 && index !== current.value) {
    goTo(index);
  }
}

function getSlideStyle(index: number) {
  if (images.value.length < 3) {
    return {};
  }
  const total = images.value.length;
  const cur = current.value;

  let width = '0px';
  let marginRight = '0px';
  let marginLeft = '0px';
  let maskOpacity = '0';

  if (index === cur) {
    maskOpacity = '1';
    if (cur > 0 && cur < total - 1) {
      width = 'calc(100% - 348px)';
    } else {
      width = 'calc(100% - 174px)';
    }
  } else if (index === cur - 1) {
    width = '150px';
    marginRight = 'var(--space-3)';
  } else if (index === cur + 1) {
    width = '150px';
    marginLeft = 'var(--space-3)';
  }

  return {
    '--slide-width-lg': width,
    '--slide-margin-right-lg': marginRight,
    '--slide-margin-left-lg': marginLeft,
    '--slide-mask-opacity-lg': maskOpacity,
  };
}

const bannerRef = ref<HTMLElement | null>(null);
const isInViewport = ref(true);
const isDocumentVisible = ref(true);

let observer: IntersectionObserver | null = null;

function handleVisibilityChange(): void {
  isDocumentVisible.value = document.visibilityState === 'visible';
}

onMounted(() => {
  if (!hasControls.value) {
    return;
  }

  // 监听视口可见性（仅限浏览器支持）
  if (typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // 被覆盖不超过 20%（即至少 80% 可见）时才允许滚动
        isInViewport.value = entry.isIntersecting && entry.intersectionRatio >= 0.8;
      });
    }, { threshold: 0.8 });

    if (bannerRef.value) {
      observer.observe(bannerRef.value);
    }
  }

  // 监听文档可见性
  document.addEventListener('visibilitychange', handleVisibilityChange);

  timer = setInterval(() => {
    if (!paused.value && isInViewport.value && isDocumentVisible.value) {
      next();
    }
  }, AUTOPLAY_INTERVAL_MS);
});

onUnmounted(() => {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  if (observer !== null) {
    observer.disconnect();
    observer = null;
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

function dotAriaLabel(index: number): string {
  return copy.sections.banner.dotAria.replace('N', String(index + 1));
}

function slideLoading(index: number): 'eager' | 'lazy' {
  return index === FIRST_SLIDE_INDEX ? 'eager' : 'lazy';
}

function slideFetchPriority(index: number): 'high' | 'auto' {
  return index === FIRST_SLIDE_INDEX ? 'high' : 'auto';
}
</script>

<template>
  <section
    :id="props.id"
    ref="bannerRef"
    :class="$style.banner"
    data-section-type="banner"
    @mouseenter="paused = true"
    @mouseleave="paused = false"
  >
    <div v-if="images.length > 0" :class="[$style.carousel, images.length >= 3 && $style.carouselAccordion]">
      <div :class="$style.inner" :style="innerStyle" :data-current="current">
        <figure
          v-for="(image, index) in images"
          :key="`${image.url}-${index}`"
          :class="[$style.item, index === current && $style.activeItem]"
          :style="getSlideStyle(index)"
          @click="handleItemClick(index)"
        >
          <FallbackImage
            :class="$style.slide"
            :src="image.url"
            :alt="hasText(image.caption) ? image.caption : props.section.title"
            :context="`banner section='${props.section.id}' image#${index} url=${image.url}`"
            :loading="slideLoading(index)"
            :fetch-priority="slideFetchPriority(index)"
          />
          <figcaption
            v-if="hasText(image.caption)"
            :class="[$style.caption, index === current && $style.captionActive]"
          >
            {{ image.caption }}
          </figcaption>
        </figure>
      </div>

      <template v-if="hasControls">
        <button
          type="button"
          :class="[$style.control, $style.prev]"
          :aria-label="copy.sections.banner.prevAria"
          @click="prev"
        >❮</button>
        <button
          type="button"
          :class="[$style.control, $style.next]"
          :aria-label="copy.sections.banner.nextAria"
          @click="next"
        >❯</button>

        <div :class="$style.dots" role="tablist">
          <button
            v-for="(image, index) in images"
            :key="`dot-${index}`"
            type="button"
            :class="[$style.dot, { [$style.active]: index === current }]"
            :data-active="index === current ? '' : undefined"
            :aria-label="dotAriaLabel(index)"
            @click="goTo(index)"
          ></button>
        </div>
      </template>
    </div>

    <div v-else :class="$style.empty" role="status" data-testid="banner-empty">
      <p :class="$style.emptyTitle">{{ copy.fallback.section.title }}</p>
      <p :class="$style.emptyHint">{{ copy.sections.banner.dataMissing }}</p>
    </div>
  </section>
</template>

<style module>
/* 全宽 Section：占满 HomePage 主容器 grid 整行（Q7 / ADR-0012）。 */
.banner {
  grid-column: 1 / -1;
}

.carousel {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  background: var(--color-bg-secondary);
}

.inner {
  display: flex;
  width: 100%;
  transition: transform var(--template-carousel-transition) ease-in-out;
}

.item {
  position: relative;
  min-width: 100%;
  margin: 0;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--color-bg-primary);
}

/* FallbackImage 根 <img> 透传的类，叠加高度约束对齐模板 300px 裁切。 */
.slide {
  width: 100%;
  height: var(--template-carousel-height);
  object-fit: fill;
}

.caption {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  margin: 0;
  padding: var(--space-1) var(--space-3);
  color: var(--color-bg-primary);
  background: rgba(0, 0, 0, 0.4);
  font-size: var(--font-size-sm);
}

.control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding: var(--template-carousel-control-padding);
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: rgba(255, 255, 255, 0.7);
  font-size: var(--template-carousel-control-font);
  cursor: pointer;
  user-select: none;
  transition:
    background var(--template-hover-duration) ease,
    color var(--template-hover-duration) ease;
}

.control.prev {
  left: 0;
}

.control.next {
  right: 0;
}

.control:hover {
  background: rgba(0, 0, 0, 0.6);
  color: var(--color-bg-primary);
}

.dots {
  position: absolute;
  bottom: var(--space-2);
  width: 100%;
  text-align: center;
}

.dot {
  margin: 0 var(--space-1);
  padding: 0;
  width: var(--template-carousel-dot-size);
  height: var(--template-carousel-dot-size);
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: background-color var(--template-hover-duration) ease;
}

.dot.active,
.dot:hover {
  background: var(--color-brand-primary);
}

.empty {
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  background: var(--color-bg-secondary);
  text-align: center;
}

.emptyTitle {
  margin: 0 0 var(--space-1);
  color: var(--color-brand-primary);
  font-size: var(--font-size-md);
}

.emptyHint {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

@media (min-width: 1024px) {
  .carouselAccordion .inner {
    transform: none !important;
    display: flex;
    gap: 0;
    width: 100%;
  }

  .carouselAccordion .item {
    min-width: 0;
    width: var(--slide-width-lg);
    margin-right: var(--slide-margin-right-lg);
    margin-left: var(--slide-margin-left-lg);
    overflow: hidden;
    border-radius: 12px;
    transition:
      width var(--template-carousel-transition) ease-in-out,
      margin-right var(--template-carousel-transition) ease-in-out,
      margin-left var(--template-carousel-transition) ease-in-out;
    cursor: pointer;
  }

  .carouselAccordion .item::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 150px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%);
    pointer-events: none;
    opacity: var(--slide-mask-opacity-lg);
    transition: opacity var(--template-carousel-transition) ease-in-out;
  }

  .carouselAccordion .slide {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }

  .carouselAccordion .caption {
    position: absolute;
    left: 36px;
    bottom: 32px;
    width: auto;
    max-width: 80%;
    margin: 0;
    padding: 0;
    color: var(--color-bg-primary);
    background: transparent;
    font-size: 24px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
    opacity: 0;
    transform: translateY(15px);
    transition:
      opacity var(--template-carousel-transition) ease-in-out,
      transform var(--template-carousel-transition) ease-in-out;
    pointer-events: none;
  }

  .carouselAccordion .captionActive {
    opacity: 1;
    transform: translateY(0);
  }

  .carouselAccordion .control {
    display: none;
  }
}
</style>
